// src/lib/rcDial.js — the one place that knows how to put a call through.
//
// RingCentral two-legged dial: RC rings the principal's own device first, then
// connects the destination, so the recipient sees the Chatrone DID instead of a
// personal cell. `api/rc-dial.js` writes the `call_log` row itself and
// `api/rc-post-call.js` later enriches it with the RingSense transcript + Claude
// analysis — so the RC path needs no manual log entry from the UI.
//
// Falls back to the native dialer (`tel:`) whenever RC can't place the call. In
// the iOS shell Capacitor cancels the navigation and hands the tel: URL to the
// system Phone app (`tel` is declared in ios/App/App/Info.plist). That fallback
// leaves no server-side row, so it reports needsManualLog: true and the caller
// is expected to open QuickLog on return.
//
// VA-SAFETY T1: only ever invoked from a human tap. Nothing here dials on its own.

import { getSessionWithTimeout } from './authSession'

/** Dial straight from the device. Returns the outcome shape placeCall uses. */
export function dialNatively(toNumber) {
  // Keep a leading + — stripping it breaks international numbers.
  const dialable = String(toNumber || '').replace(/[^\d+]/g, '')
  window.location.href = `tel:${dialable}`
  return { ok: true, via: 'tel', callLogId: null, needsManualLog: true }
}

/**
 * Place a call to a contact.
 * @returns {Promise<{ok, via: 'ringcentral'|'tel'|null, callLogId, needsManualLog, error?}>}
 */
export async function placeCall({ toNumber, contactId = null, contactName = null }) {
  const number = String(toNumber || '').trim()
  if (!number) {
    return { ok: false, via: null, callLogId: null, needsManualLog: false, error: 'No phone number' }
  }

  try {
    const { data: { session } } = await getSessionWithTimeout()
    const res = await fetch('/api/rc-dial', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({ toNumber: number, contactId, contactName }),
    })
    const data = await res.json().catch(() => ({}))

    if (res.ok && data.ok) {
      return { ok: true, via: 'ringcentral', callLogId: data.callLogId || null, needsManualLog: false }
    }
    // rc-dial answers 200 { ok:false, fallback:{ manualDial } } on RC-side failure
    throw new Error(data.error || `RingCentral dial failed (${res.status})`)
  } catch (err) {
    console.warn('rc-dial failed, falling back to native dialer:', err.message)
    return { ...dialNatively(number), error: err.message }
  }
}
